import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

const navLinks = [
  { href: "/", label: "Accueil" },
  { href: "/a-propos", label: "À propos" },
  { href: "/portfolio", label: "Portfolio" },
  { href: "/flashs", label: "Flashs" },
  { href: "/strass-dentaire", label: "Strass dentaire" },
  { href: "/shop", label: "Shop" },
  { href: "/faq", label: "FAQ" },
  { href: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-background/95 backdrop-blur-sm border-b border-border/40" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="font-heading text-2xl md:text-3xl font-light tracking-widest"
            data-testid="logo-link"
          >
            NYMPHEA
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-xs uppercase tracking-widest transition-colors hover:text-foreground link-underline ${
                  location.pathname === link.href
                    ? "text-foreground nav-link-active"
                    : "text-muted-foreground"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="https://instagram.com/nymphea.ttt"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="instagram-link-desktop"
            >
              <Instagram className="w-5 h-5" strokeWidth={1.5} />
            </a>
            <Link to="/demande-projet" data-testid="cta-project-desktop">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 uppercase tracking-widest text-xs">
                Demande de projet
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="w-6 h-6" strokeWidth={1.5} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-96 p-0">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-6 border-b border-border/40">
                  <span className="font-heading text-2xl font-light tracking-widest">NYMPHEA</span>
                  <SheetClose asChild>
                    <Button variant="ghost" size="icon" data-testid="mobile-menu-close">
                      <X className="w-6 h-6" strokeWidth={1.5} />
                    </Button>
                  </SheetClose>
                </div>
                <nav className="flex-1 p-6">
                  <ul className="space-y-6">
                    {navLinks.map((link) => (
                      <li key={link.href}>
                        <SheetClose asChild>
                          <Link
                            to={link.href}
                            className={`block text-lg font-light ${
                              location.pathname === link.href
                                ? "text-foreground"
                                : "text-muted-foreground"
                            }`}
                            data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                          >
                            {link.label}
                          </Link>
                        </SheetClose>
                      </li>
                    ))}
                  </ul>
                </nav>
                <div className="p-6 border-t border-border/40 space-y-4">
                  <SheetClose asChild>
                    <Link to="/demande-projet" className="block" data-testid="cta-project-mobile">
                      <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 uppercase tracking-widest text-xs w-full">
                        Demande de projet
                      </Button>
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/rendez-vous" className="block" data-testid="cta-rdv-mobile">
                      <Button variant="outline" className="h-12 px-8 uppercase tracking-widest text-xs w-full border-foreground text-foreground hover:bg-foreground hover:text-background">
                        Prendre rendez-vous
                      </Button>
                    </Link>
                  </SheetClose>
                  <div className="flex items-center justify-center gap-4 pt-4">
                    <a
                      href="https://instagram.com/nymphea.ttt"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="instagram-link-mobile"
                    >
                      <Instagram className="w-6 h-6" strokeWidth={1.5} />
                    </a>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
